import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type MarketTotalRevenueController from 'lumen/controllers/market/total-revenue';
import type {
  TotalRevenueDashboard,
  TotalRevenuePortfolio,
  TotalRevenueSegmentationBreakdowns,
  TotalRevenueTrend,
} from 'lumen/models';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import {
  APIGATEWAY,
  type DateAggregationType,
} from 'lumen/utils/constants/general-constants';
import type {
  Metric,
  View,
} from 'lumen/utils/constants/total-revenue-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export type MarketTotalRevenueParams = {
  dateFilterFrom?: string;
  dateFilterTo?: string;
  departmentId?: string;
  focusedProperties: string[];
  fromDate?: string;
  isPerAvailableRoom: boolean;
  localReference: string;
  portfolios: string[];
  properties: string[];
  referencePeriodCustomDate?: string;
  referencePeriods: string[];
  toDate?: string;
  trendAggregation: DateAggregationType;
  trendMetric: Metric;
  view: View;
};

/**
 * Returns the property ids to be used in the query param.
 * There are 3 cases:
 * 1. Some properties are selected (i.e. "All Properties" not selected) => use the selected properties
 * 2. All properties of all portfolios are selected => []
 * 3. All properties of some portfolios are selected => map the selected portfolios to their properties
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getPropertyIdsQueryParam(
  myHotelsPortfolios: TotalRevenuePortfolio[],
  selectedPortfolios: string[],
  selectedProperties: string[],
) {
  const isAllPortfoliosSelected = selectedPortfolios.length === 0;
  const isAllPropertiesSelected = selectedProperties.length === 0;

  // Case 1
  if (!isAllPropertiesSelected) {
    return [...selectedProperties];
  }

  // Case 2
  if (isAllPortfoliosSelected && isAllPropertiesSelected) {
    return [];
  }

  // Case 3
  return (
    myHotelsPortfolios
      // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
      .filter((p) => selectedPortfolios.includes(p.id))
      .flatMap((p) => p.hotels.map((h) => h.id))
  );
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function isOnlyQPChanged(transition: Transition, key: string) {
  if (!transition.from) {
    return false;
  }

  const oldQPs = transition.from.queryParams;
  const newQPs = transition.to.queryParams;

  const isQPChanged = newQPs[key] !== oldQPs[key];
  const areOtherQPsUnchanged = Object.keys(newQPs)
    .filter((k) => k !== key)
    .every((k) => oldQPs[k] === newQPs[k]);

  return isQPChanged && areOtherQPsUnchanged;
}

export default class MarketTotalRevenueRoute extends Route {
  queryParams = {
    view: {
      refreshModel: true,
    },
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    referencePeriods: {
      refreshModel: true,
    },
    referencePeriodCustomDate: {
      refreshModel: true,
    },
    portfolios: {
      refreshModel: true,
    },
    properties: {
      refreshModel: true,
    },
    dateFilterFrom: {
      refreshModel: true,
    },
    dateFilterTo: {
      refreshModel: true,
    },
    isPerAvailableRoom: {
      refreshModel: false,
    },
    focusedProperties: {
      refreshModel: true,
    },
    trendMetric: {
      refreshModel: false,
    },
    trendAggregation: {
      refreshModel: false,
    },
    localReference: {
      refreshModel: true, // TODO TOR-300: Temporary fix until backend can fetch all reference periods at once
    },
    departmentId: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare totalRevenue: Services['total-revenue'];
  @service('total-revenue/date')
  declare totalRevenueDate: Services['total-revenue/date'];
  @service('total-revenue/myhotels')
  declare totalRevenueMyhotels: Services['total-revenue/myhotels'];
  @service declare transition: Services['transition'];

  calculateAndAssignCapacityTask = task(
    async (
      requestModel: TotalRevenueTrend | TotalRevenueDashboard,
      params: MarketTotalRevenueParams,
    ) => {
      const { fromDate, toDate } = this.getDateRangeInLocalTime(params);

      const areAllPortfoliosSelected = params.portfolios.length === 0;
      const areAllPropertiesSelected = params.properties.length === 0;
      const hasFocusedProperties = params.focusedProperties.length !== 0;

      await this.totalRevenueMyhotels.myHotelsTaskInstance;
      const myHotels = this.totalRevenueMyhotels.portfolios
        .filter(
          // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
          (p) => areAllPortfoliosSelected || params.portfolios.includes(p.id),
        )
        .flatMap((p) => [...p.hotels])
        .filter(
          // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
          (h) => areAllPropertiesSelected || params.properties.includes(h.id),
        )
        .filter(
          (h) =>
            // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string'.
            !hasFocusedProperties || params.focusedProperties.includes(h.id),
        );

      requestModel.calculateAndAssignCapacity(fromDate, toDate, ...myHotels);

      return requestModel;
    },
  );

  requestSegmentationBreakdownsTask = task(
    { restartable: true },
    async (params: MarketTotalRevenueParams) => {
      const queryParams = await this.getQueryParams(params);

      const referencePeriods = [queryParams.localReference];
      const referencePeriodCustomDate =
        queryParams.localReference === 'custom'
          ? queryParams.referencePeriodCustomDate
          : undefined;

      try {
        const segmentationBreakdowns =
          await this.store.queryRecord<TotalRevenueSegmentationBreakdowns>(
            'total-revenue/segmentation-breakdowns',
            {
              namespace: APIGATEWAY,
              typeKey: 'totalrevenue/segmentationbreakdowns',
              market_id: this.hotelAndMarketService.market.id,
              currency: queryParams.currency,
              from_date: queryParams.fromDate,
              to_date: queryParams.toDate,
              reference_periods: referencePeriods,
              custom_from_date: referencePeriodCustomDate,
              subscription_ids:
                queryParams.focusedPropertyIds.length > 0
                  ? queryParams.focusedPropertyIds
                  : queryParams.propertyIds, // Focused properties will always be a subset of all properties
            },
          );

        return segmentationBreakdowns;
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestTotalRevenueDashboardTask = task(
    { restartable: true },
    async (params: MarketTotalRevenueParams) => {
      const queryParams = await this.getQueryParams(params);

      try {
        const totalRevenueDashboard =
          await this.store.queryRecord<TotalRevenueDashboard>(
            'total-revenue/dashboard',
            {
              namespace: APIGATEWAY,
              typeKey: 'totalrevenue/dashboard',
              market_id: queryParams.marketId,
              currency: queryParams.currency,
              from_date: queryParams.fromDate,
              to_date: queryParams.toDate,
              reference_periods: queryParams.referencePeriods,
              custom_from_date: queryParams.referencePeriodCustomDate,
              subscription_ids: queryParams.propertyIds,
              focused_subscription_ids: queryParams.focusedPropertyIds,
            },
          );

        await this.calculateAndAssignCapacityTask.perform(
          // @ts-expect-error: Argument of type 'TotalRevenueDashboard | null' is not assignable to parameter of type 'TotalRevenueDashboard | TotalRevenueTrend'.
          totalRevenueDashboard,
          params,
        );

        return totalRevenueDashboard;
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestTotalRevenueTrendTask = task(
    { restartable: true },
    async (params: MarketTotalRevenueParams) => {
      const queryParams = await this.getQueryParams(params);

      try {
        const totalRevenueTrend =
          await this.store.queryRecord<TotalRevenueTrend>(
            'total-revenue/trend',
            {
              namespace: APIGATEWAY,
              typeKey: 'totalrevenue/trend',
              market_id: queryParams.marketId,
              currency: queryParams.currency,
              from_date: queryParams.fromDate,
              to_date: queryParams.toDate,
              reference_periods: queryParams.referencePeriods,
              custom_from_date: queryParams.referencePeriodCustomDate,
              subscription_ids:
                queryParams.focusedPropertyIds.length > 0
                  ? queryParams.focusedPropertyIds
                  : queryParams.propertyIds, // Focused properties will always be a subset of all properties
            },
          );

        await this.calculateAndAssignCapacityTask.perform(
          // @ts-expect-error: Argument of type 'TotalRevenueTrend | null' is not assignable to parameter of type 'TotalRevenueDashboard | TotalRevenueTrend'.
          totalRevenueTrend,
          params,
        );

        return totalRevenueTrend;
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  beforeModel(transition: Transition): void {
    if (!this.access.hasTotalRevenue) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateTotalRevenueQueryParams(
        transition,
        this.paramsFor(this.routeName) as MarketTotalRevenueParams, // Use paramsFor to obtain the deserialised query params
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getDateRangeInLocalTime(params: MarketTotalRevenueParams) {
    const fromDateToUse = params.dateFilterFrom ?? params.fromDate;
    const toDateToUse = params.dateFilterTo ?? params.toDate;

    return {
      fromDate:
        this.totalRevenueDate.getFromDateInLocalTimeWithFallback(fromDateToUse),
      toDate:
        this.totalRevenueDate.getToDateInLocalTimeWithFallback(toDateToUse),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async getQueryParams(params: MarketTotalRevenueParams) {
    await this.totalRevenueMyhotels.myHotelsTaskInstance;

    const { fromDate, toDate } = this.getDateRangeInLocalTime(params);

    return {
      marketId: this.hotelAndMarketService.market.id,
      fromDate: fromDate.format('YYYY-MM-DD'),
      toDate: toDate.format('YYYY-MM-DD'),
      referencePeriods: params.referencePeriods,
      referencePeriodCustomDate: params.referencePeriodCustomDate ?? undefined,
      propertyIds: getPropertyIdsQueryParam(
        this.totalRevenueMyhotels.portfolios,
        params.portfolios,
        params.properties,
      ),
      focusedPropertyIds: params.focusedProperties,
      localReference: params.localReference,
      currency: this.totalRevenue.currency,
    } as const;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: MarketTotalRevenueParams, transition: Transition) {
    const model = {
      totalRevenueDashboard: this.requestTotalRevenueDashboardTask,
      totalRevenueTrend: this.requestTotalRevenueTrendTask,
      segmentationBreakdowns: this.requestSegmentationBreakdownsTask,
    };

    const isOnlyViewChanged = isOnlyQPChanged(transition, 'view');
    // TODO TOR-300: Remove this check once the backend can fetch all reference periods at once
    const isOnlyLocalReferenceChanged = isOnlyQPChanged(
      transition,
      'localReference',
    );

    if (!isOnlyLocalReferenceChanged && !isOnlyViewChanged) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      model.totalRevenueDashboard.perform(params);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      model.totalRevenueTrend.perform(params);
    }

    if (params.view === 'strategy') {
      const hasFetchedSegmentationBreakdowns = Boolean(
        model.segmentationBreakdowns.lastSuccessful,
      );

      if (!hasFetchedSegmentationBreakdowns || !isOnlyViewChanged) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        model.segmentationBreakdowns.perform(params);
      }
    }

    return model;
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async setupController(
    controller: MarketTotalRevenueController,
    model: ReturnType<typeof this.model>,
    transition: Transition,
  ): Promise<void> {
    super.setupController(controller, model, transition);

    await this.totalRevenueMyhotels.myHotelsTaskInstance;
    const hasStrategyTab = this.totalRevenueMyhotels.properties.some(
      (p) => p.hasTransactionToReservationLink,
    );

    if (controller.view === 'strategy' && !hasStrategyTab) {
      controller.view = 'departments';
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      model.segmentationBreakdowns.cancelAll();
    }
  }

  @action refreshRoute(): void {
    this.refresh();
  }
}
